import React, { useState, useEffect, useRef, useContext } from 'react';
import { isMobile } from 'react-device-detect';
import ReactLoading from 'react-loading';
import PropTypes from 'prop-types';
import $ from 'jquery';
import _ from 'lodash';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { AppContext } from 'contexts';
import { useSensor, useShowRotateIcon } from 'hooks';
import { Header } from 'views/shared';
import { PanoInstance } from 'types';
import { images } from 'images';

import {
  FloorPlan,
  RoomInfo,
  InfoProperty,
  Reviewer,
  Navigator,
  QRCode,
  Login,
  ChatBubbleIcon,
  ChatModal,
  ConfirmationModal,
  MessageModal,
  HangupModal,
} from '.';

const useStyles = makeStyles({
  header: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
  pano: {
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  loader: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  rotateIcon: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 1000,

    '& img': {
      width: 50,
      height: 50,
    },
  },
});

let mouseDown = false;

interface Props {
  image: string;
  instance: PanoInstance;
}

function Pano({ instance, image }: Props) {
  const classes = useStyles();
  const componentRef = useRef(null);

  const appContext = useContext(AppContext);
  const {
    setInfoPropertyDisplay,
    setFloorPlanZoom,
    setQRCodeDisplay,
    sendDataToVRServer,
  } = appContext;

  const [isLoading, setIsLoading] = useState(false);
  const { sensorEnabled, enableSensor } = useSensor();
  const { showRotateIcon, hideRotateIcon } = useShowRotateIcon();

  const lastDataSent = { x: 0, y: 0, z: 0, w: 0 };
  let lastRotationDataSent = new Date().getTime();

  const initViewer = () => {
    const control = instance?.v?.getControl();
    if (
      control?.hasOwnProperty('getAzimuthalAngle') &&
      typeof control?.getAzimuthalAngle === 'function'
    ) {
      control.rotateLeft(control.getAzimuthalAngle() - 1.5708);
      control.rotateUp(control.getPolarAngle() - 1.5708);
    }
  };

  const sendCameraRotation = () => {
    if (instance && instance.v && instance.v.camera && instance.v.camera.quaternion) {
      const { _x, _y, _z, _w } = instance.v.camera.quaternion;

      if (
        _x !== lastDataSent.x &&
        _y !== lastDataSent.y &&
        _z !== lastDataSent.z &&
        _w !== lastDataSent.w
      ) {
        const currentTime = new Date().getTime();

        if (currentTime - lastRotationDataSent > 500) {
          lastRotationDataSent = currentTime;
          lastDataSent.x = _x;
          lastDataSent.y = _y;
          lastDataSent.z = _z;
          lastDataSent.w = _w;

          const data = {
            type: 'rotation',
            content: {
              x: _x,
              y: _y,
              z: _z,
              w: _w,
            },
          };
          sendDataToVRServer(data);
        }
      }
    }
  };

  const initInstance = () => {
    instance?.p?.dispose();
    instance?.v?.remove(instance?.p);

    setIsLoading(true);

    const controls = ['fullscreen', 'video'];
    if (isMobile) {
      controls.push('setting');
    }

    const container = componentRef ? componentRef.current : null;

    instance.p = new window.PANOLENS.ImagePanorama(image);
    instance.p.addEventListener('load', () => {
      initViewer();
      return setIsLoading(false);
    });
    instance.p.addEventListener('error', () => {
      return setIsLoading(false);
    });

    if (!instance.v && container) {
      instance.v = new window.viewer({
        container,
        autoHideControlBar: false,
        controlButtons: controls,
        cameraFov: 80,
        output: 'console',
      });
    }
    instance.v.add(instance.p);
    instance.v.outputPosition();

    instance.v.addEventListener('control-change', (event: any) => {
      if (event.control === 1) {
        enableSensor();
      }
    });

    if (container) {
      const canvas = $(container).find('canvas');
      if (canvas.length) {
        const canvasElement = canvas.eq(0);
        canvasElement.css('height', window.innerHeight);
        canvasElement.css('width', window.innerWidth);
        canvasElement.on('mousedown touchstart', () => {
          mouseDown = true;
          setFloorPlanZoom(false);
          setInfoPropertyDisplay(false);
          setQRCodeDisplay(false);
        });

        canvasElement.on('mouseup touchend', () => {
          mouseDown = false;
        });

        canvasElement.on('mousemove touchmove', () => {
          if (!mouseDown) return;
          setFloorPlanZoom(false);
          setInfoPropertyDisplay(false);
          setQRCodeDisplay(false);
          hideRotateIcon();
        });
      }
    }
    if (!instance.rot_interval_id) {
      instance.rot_interval_id = setInterval(() => {
        sendCameraRotation();
      }, 300);
    }
  };

  const handleResize = () => {
    if (instance && image) {
      initInstance();
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    if (instance && image) {
      initInstance();
    }
  }, [instance, image]);

  useEffect(() => {
    if (sensorEnabled) {
      initInstance();
    }
  }, [sensorEnabled]);

  return (
    <div className={classes.pano} ref={componentRef}>
      {showRotateIcon === 'show' ? (
        <div className={classNames(classes.rotateIcon, { show: !!showRotateIcon })}>
          <img src={images.drag360White} alt='Rotate' />
          <p style={{ fontSize: '1em', color: 'white', marginTop: 15 }}>
            APPUYEZ ET FAITES GLISSER
          </p>
        </div>
      ) : null}

      {isLoading ? (
        <ReactLoading
          className={classes.loader}
          type='spinningBubbles'
          color='#ffffff'
          width='80px'
          height='80px'
        />
      ) : null}

      <Header className={classes.header} />
      <FloorPlan />
      <Navigator />
      <RoomInfo />
      <InfoProperty />
      <QRCode />
      <Reviewer />
      <Login />
      <ChatBubbleIcon />
      <ChatModal />
      <ConfirmationModal />
      <MessageModal />
      <HangupModal />
    </div>
  );
}

Pano.defaultProps = {
  image: '',
  instance: null,
};

Pano.propTypes = {
  image: PropTypes.string,
  instance: PropTypes.any,
};

export default Pano;
